'use client'

import { useSolana } from '@dewicats/connect-button'
import { Attribute } from '@models/Attributes'
import { Nft } from '@models/Nft'
import { useWallet } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import { getDeWiCatCollectables } from '@utils/solanaUtils'
import { Routes } from 'src/app/holdings/[route]/page'
import { useCallback, useMemo, useState } from 'react'
import { useAsync } from 'react-async-hook'

export type Holding = {
  publicKey: PublicKey
  name: string
  number: string
  attribute: Attribute
  imageURL: string
  status: string
  aura: boolean
}

export const useHoldings = (selectedRoute: Routes[number]) => {
  const [holdings, setHoldings] = useState<Holding[]>([])
  const wallet = useWallet()
  const solana = useSolana()
  const connected = wallet.connected

  const getCollectablesHandler = async () => {
    try {
      if (!connected) return []

      const collectables = await getDeWiCatCollectables(solana?.anchorProvider)

      const nfts = collectables.map((item) => Nft.fromJson(item))

      const initialHoldings = sortHoldings(
        nfts.map((nft) => ({
          publicKey: new PublicKey(nft.id),
          name: nft.name,
          imageURL: nft.imageURL,
          number: nft.number,
          attribute: nft.attribute,
          aura:
            !!nft.attribute.aura && nft.attribute.aura.toUpperCase() !== 'NONE',
          status: 'referred',
        })),
      )

      setHoldings(initialHoldings)

      return initialHoldings
    } catch (error) {
      throw error
    }
  }

  const { loading, error, reset } = useAsync(getCollectablesHandler, [
    solana?.anchorProvider?.wallet?.publicKey,
  ])

  const sortHoldings = (holdings: Holding[]) => {
    return holdings.sort((a, b) => parseInt(a.number) - parseInt(b.number))
  }

  const stakeHoldingHandler = useCallback(
    (holding: Holding) => {
      const { name } = holding
      const status = holdings.some(
        (holding) => holding.name === name && holding.status === 'staking',
      )
        ? 'referred'
        : 'staking'
      const newHolding = {
        ...holding,
        status,
      }
      setHoldings((prev) =>
        sortHoldings([
          ...prev.filter((holding) => holding.name !== name),
          newHolding,
        ]),
      )
    },
    [holdings],
  )

  const isAnyStaking = useMemo(
    () => holdings?.some((holding) => holding.status === 'staking'),
    [holdings],
  )

  const filteredHoldings = useMemo(() => {
    if (!holdings) return []

    switch (selectedRoute) {
      case 'all':
        return holdings
      // case 'staking':
      //   return holdings.filter((holding) => holding.status === 'staking')
      // case 'holding':
      //   return holdings.filter((holding) => holding.status !== 'staking')
    }
  }, [holdings])

  const stakeAllHandler = useCallback(() => {
    if (!holdings) return

    const status = isAnyStaking ? 'referred' : 'staking'

    const newHoldings = sortHoldings(
      holdings.map((holding) => ({
        ...holding,
        status,
      })),
    )

    setHoldings(newHoldings)
  }, [holdings, isAnyStaking])

  return {
    loading,
    error,
    connected,
    isAnyStaking,
    holdings,
    filteredHoldings,
    onRetry: reset,
    stakeHoldingHandler,
    stakeAllHandler,
  }
}
